import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, writesText } from '../skillUsage';

export const splitFlapBoardSkill: CodeoSkill = {
    name: SKILL_NAMES.splitFlapBoard,
    kind: 'codeo',
    description:
        'Flip a word into place on split-flap panels, the way a departure board resolves — for a ' +
        'destination, a status, or a word that should feel announced by a machine.',
    checkLooksFor: 'a rotationX flip, with the text written onto the element from script',
    documentUsesSkill: check((script) => /rotationX/.test(script) && writesText(script)),
    instructions: `# Split-Flap Board

Each character sits on a hinged panel that flips through intermediate letters before settling. Characters resolve left to right with a mechanical clatter you can almost hear. It is nostalgic, specific, and loud — a strong choice for a destination, an arrival, or a status change, and the wrong one for anything that should feel modern.

The flip is a \`rotationX\` on a panel with a mid-tween character swap, so the panel appears to turn over and land showing something new.

## Markup

One panel per character, each split visually by a hairline across the middle. Fix the width so proportional characters cannot shift the board.

\`\`\`html
<div id="board" style="position: absolute; left: 480px; top: 440px; display: flex; gap: 10px; perspective: 900px;">
  <div class="flap" style="position: relative; width: 116px; height: 160px; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: var(--l5-color-1, #14161b); font-family: var(--l5-font-0), monospace; font-size: 116px; font-weight: 700; color: var(--l5-color-0, #f4f6f9); box-shadow: inset 0 -1px 0 rgba(255,255,255,0.16);">A</div>
  <div class="flap" style="position: relative; width: 116px; height: 160px; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: var(--l5-color-1, #14161b); font-family: var(--l5-font-0), monospace; font-size: 116px; font-weight: 700; color: var(--l5-color-0, #f4f6f9); box-shadow: inset 0 -1px 0 rgba(255,255,255,0.16);">A</div>
  <div class="flap" style="position: relative; width: 116px; height: 160px; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: var(--l5-color-1, #14161b); font-family: var(--l5-font-0), monospace; font-size: 116px; font-weight: 700; color: var(--l5-color-0, #f4f6f9); box-shadow: inset 0 -1px 0 rgba(255,255,255,0.16);">A</div>
</div>
\`\`\`

The inset shadow across the middle is the hinge line; without it the panels read as plain tiles.

## Timeline code

Each panel steps through a fixed number of flips. The character shown is a pure function of the tween value, so seeking is safe.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const TARGET = 'NOW';
const ALPHABET = ' ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const FLIPS = 7;
const flapEls = document.querySelectorAll('.flap');

flapEls.forEach((flap, index) => {
    const step = { value: 0 };
    const finalIndex = ALPHABET.indexOf(TARGET[index]);
    tl.to(step, {
        value: FLIPS,
        duration: 0.9,
        ease: 'power2.out',
        onUpdate: () => {
            const flipped = Math.floor(step.value);
            flap.textContent =
                flipped >= FLIPS
                    ? TARGET[index]
                    : ALPHABET[(finalIndex - (FLIPS - flipped) * 3 + ALPHABET.length * 4) % ALPHABET.length];
            // The panel is mid-turn between whole steps, so it reads as hinging rather than cutting.
            const phase = step.value - flipped;
            gsap.set(flap, { rotationX: flipped >= FLIPS ? 0 : -phase * 70 });
        },
    }, 0.2 + index * 0.13);

    // The landing accent belongs on the timeline, not in a callback. Placed at the
    // instant this panel settles, it is found by seeking like everything else.
    const lands = 0.2 + index * 0.13 + 0.9;
    tl.to(flap, { filter: 'brightness(1.35)', duration: 0.08, ease: 'none' }, lands);
    tl.to(flap, { filter: 'brightness(1)', duration: 0.22, ease: 'power2.out' }, lands + 0.08);
});

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Flip count**: 7 reads as a board searching for its letter. Below 4 it barely churns; above 12 the board is a blur.
- **Stagger**: 0.13s per character resolves left to right at a readable pace. Drop to 0.06 for a longer word so the whole board still lands inside a second.
- **Alphabet**: include digits for a flight number or a time; restrict to the target's own letters for a gentler, less searching flip.
- **Vertical roll instead**: for digits, \`${SKILL_NAMES.numberWheelRoll}\` is the smoother sibling — wheels rather than flaps.
- **Landing accent**: a very short brightness lift on each panel as it settles makes the resolution read as a click.
- **Board furniture**: a header row or a category label above the panels commits to the departure-board reference; without it the panels read as generic tiles.

## Pitfalls

- Panels are \`rotationX\`-tweened, so none may carry a CSS \`transform\`; \`perspective\` belongs on the board container.
- Fix each panel's width and use a monospace face, or letters of different widths make the board ripple.
- **Never create a tween inside a callback.** \`onComplete: () => gsap.to(...)\` spawns a second animation driven by GSAP's own ticker, and the host only ever *seeks* this timeline — it never plays it. A callback-spawned tween therefore fires against wall-clock time or not at all, and the frame stops being a pure function of progress. Anything that should happen at a moment goes on the timeline at that moment.
- Never derive the intermediate character from a clock or a random draw — it must come from the tween value, or every rendered frame disagrees.
- Three to six characters. A split-flap sentence takes far too long to resolve and nobody waits.
- \`textContent\` is rewritten every frame, so nothing else may live inside a panel.
- The board is completely still once it lands. Give the scene something else — see \`${SKILL_NAMES.ambientFloatLoop}\`.
- This is a heavy stylistic commitment. If the video's design language is clean and modern, \`${SKILL_NAMES.scrambleDecodeReveal}\` gives a similar resolve without the nostalgia.`,
};
